import { Coins, Gem, Hexagon, Mountain, TreePine } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export function Meter({ value, tone }: { value: number; tone: "hp" | "enemy" | "atb" | "xp" }) {
  const normalized = Math.max(0, Math.min(100, value));
  const previous = useRef(normalized);
  const [trail, setTrail] = useState(normalized);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (tone === "atb") {
      previous.current = normalized;
      return;
    }
    const before = previous.current;
    previous.current = normalized;
    if (Math.abs(normalized - before) < 0.001) return;
    setChanging(true);
    const pulse = window.setTimeout(() => setChanging(false), 260);
    let settle = 0;
    if (normalized < before) {
      setTrail(before);
      settle = window.setTimeout(() => setTrail(normalized), 240);
    } else {
      setTrail(normalized);
    }
    return () => {
      window.clearTimeout(pulse);
      if (settle) window.clearTimeout(settle);
    };
  }, [normalized, tone]);

  return (
    <div
      className={`meter meter-${tone}${changing ? " is-changing" : ""}${tone === "atb" && normalized >= 90 ? " is-ready" : ""}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(normalized)}
    >
      {tone !== "atb" && <span className="meter-trail" style={{ width: `${trail}%` }} />}
      <span className="meter-fill" style={{ width: `${normalized}%` }} />
      {tone === "atb" && <span className="meter-marker" />}
    </div>
  );
}

export type ResourceKind = "wood" | "ore" | "gold" | "crystal";

export type RewardKind = ResourceKind | "diamond";

export const RESOURCE_LABELS: Record<ResourceKind, string> = {
  wood: "木材",
  ore: "矿石",
  gold: "金币",
  crystal: "结晶",
};

export function ResourceIcon({ kind, size = 15 }: { kind: RewardKind; size?: number }) {
  const Icon =
    kind === "wood"
      ? TreePine
      : kind === "ore"
        ? Mountain
        : kind === "gold"
          ? Coins
          : kind === "crystal"
            ? Hexagon
            : Gem;
  return (
    <Icon
      className={`resource-icon resource-icon-${kind}`}
      size={size}
      strokeWidth={2.1}
      aria-hidden="true"
    />
  );
}

export function Resource({
  kind,
  value,
  limit,
}: {
  kind: ResourceKind;
  value: number;
  limit?: number | null;
}) {
  const limitLabel = limit === undefined ? "" : limit === null ? "无上限" : `上限 ${limit}`;
  const label = `${RESOURCE_LABELS[kind]} ${value}${limitLabel ? `，${limitLabel}` : ""}`;
  return (
    <div className={`resource resource-${kind}`} aria-label={label} title={label}>
      <i>
        <ResourceIcon kind={kind} />
      </i>
      <span className="resource-amount">
        <strong>{value}</strong>
        {limitLabel && <small>{limitLabel}</small>}
      </span>
    </div>
  );
}

export function Status({
  icon,
  label,
  value,
  effectTarget,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  effectTarget?: string;
}) {
  return (
    <div className="status-item" data-effect-target={effectTarget}>
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
